<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { ArrowRight, BadgeCheck, Database, Lock, Shield } from 'lucide-vue-next';
import { route } from 'ziggy-js';

const heroImage = '/storage/images/hero-cybersecurity.jpg';
</script>

<template>
    <section id="home" class="relative flex min-h-screen items-center justify-center overflow-hidden">
        <div class="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-25" :style="{ backgroundImage: `url(${heroImage})` }" />
        <div class="absolute inset-0 bg-background/70" />

        <div class="relative z-10 container mx-auto px-4 py-24">
            <div class="mx-auto max-w-4xl text-center">
                <div class="glass-card mb-8 inline-flex items-center space-x-2 px-5 py-3">
                    <Shield class="h-5 w-5 text-primary" />
                    <span class="text-sm font-medium">Digital certificate platform for cash assets</span>
                </div>

                <h1 class="mb-6 bg-gradient-to-r from-primary via-white to-accent bg-clip-text text-transparent">
                    Protect banknotes with digital proof, verification and traceability
                </h1>

                <p class="mb-8 text-xl leading-relaxed text-muted-foreground">
                    Digital Safe helps households, businesses and institutions register valuable cash, create tamper-resistant certificates and
                    verify asset history with blockchain-backed transparency.
                </p>

                <div class="mb-12 flex flex-col items-center justify-center gap-4 sm:flex-row">
                    <Link :href="route('about')">
                        <button class="btn-hero glow-hover flex cursor-pointer items-center gap-3">
                            Explore Digital Safe
                            <ArrowRight class="h-5 w-5" />
                        </button>
                    </Link>
                    <Link :href="route('whitepaper')">
                        <button class="btn-ghost cursor-pointer">Read the whitepaper</button>
                    </Link>
                </div>

                <div class="mx-auto mb-14 grid max-w-3xl grid-cols-1 gap-3 text-sm text-muted-foreground sm:grid-cols-3">
                    <div class="flex items-center justify-center gap-2">
                        <BadgeCheck class="h-4 w-4 text-primary" />
                        <span>Banknote registration</span>
                    </div>
                    <div class="flex items-center justify-center gap-2">
                        <BadgeCheck class="h-4 w-4 text-primary" />
                        <span>Certificate verification</span>
                    </div>
                    <div class="flex items-center justify-center gap-2">
                        <BadgeCheck class="h-4 w-4 text-primary" />
                        <span>Ownership documentation</span>
                    </div>
                </div>

                <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
                    <div class="glass-card glow-hover text-center">
                        <div class="glow mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-hero">
                            <Shield class="h-6 w-6 text-primary-foreground" />
                        </div>
                        <h3 class="mb-2 text-lg font-semibold">Register Assets</h3>
                        <p class="text-sm text-muted-foreground">Create a structured digital record for selected banknotes and cash holdings.</p>
                    </div>

                    <div class="glass-card glow-hover text-center">
                        <div class="glow mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-hero">
                            <Lock class="h-6 w-6 text-primary-foreground" />
                        </div>
                        <h3 class="mb-2 text-lg font-semibold">Issue Certificates</h3>
                        <p class="text-sm text-muted-foreground">Generate verifiable certificates designed to support identification and proof.</p>
                    </div>

                    <div class="glass-card glow-hover text-center">
                        <div class="glow mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-hero">
                            <Database class="h-6 w-6 text-primary-foreground" />
                        </div>
                        <h3 class="mb-2 text-lg font-semibold">Verify History</h3>
                        <p class="text-sm text-muted-foreground">Use transparent records to check authenticity, status and documented ownership.</p>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
